import React, { useState, useEffect,useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useCategory } from "../context/CategoryContext";
import Sidebar from "../components/Sidebar";
import dayjs from "dayjs";
import { showError, showSuccess } from "../utils/toast"; // adjust path
import { useNotification } from "../context/NotificationContext";
import { Modal, Button, Toast, ToastContainer } from "react-bootstrap";
const AgentDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [policies, setPolicies] = useState([]);
  const { selectedCategory, switchCategory } = useCategory();
  const { notificationsCount, fetchNotificationCount } = useNotification();
  const [showModal, setShowModal] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [message, setMessage] = useState("");
  
  // Date range filter states
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null
  });
  const [showCalendar, setShowCalendar] = useState(false);


  useEffect(() => {
    const stored = localStorage.getItem("selectedInsuranceType");
    if (stored === "General Insurance" || stored === "Life Insurance") {
      switchCategory(stored);
    } else {
      localStorage.setItem("selectedInsuranceType", "Life Insurance");
      switchCategory("Life Insurance");
    }
  }, []);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  const handleSwitchCategory = (e) => {
    const newValue = e.target.value;
    switchCategory(newValue);
    localStorage.setItem("selectedInsuranceType", newValue);
    window.location.reload();
  };

  useEffect(() => {
    const fetchPolicies = async () => {
      try {
        const agent = user || JSON.parse(localStorage.getItem("user"));
        if (!agent?.id) return;
        const categoryParam = selectedCategory;

        const res = await fetch(
          `${import.meta.env.VITE_API_URL}/api/Insurance-by-Category?insuranceType=${categoryParam}&agentId=${agent.id}`
        );
        const data = await res.json();
        if (res.ok && Array.isArray(data)) setPolicies(data);
      } catch (err) {
        console.error("Failed to fetch policies:", err);
      }
    };
    fetchPolicies();
  }, [user, selectedCategory]);

  //fetch Notification
  // fetch notifications once agent known
  useEffect(() => {
    if (user?.id) fetchNotificationCount();
  }, [user?.id, fetchNotificationCount]);


  // for send reminder
  const handleSendMessage = async (person, message) => {
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/v1/notification/send-notification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: message,
          phone: person.customerPhone,
          agentId: person.agentId,
          policyId: person._id || null,
        }),
      });

      if (!res.ok) { showError("Failed to send notification"); } else {
        showSuccess("Message sent successfully!");
      }
    } catch (error) {
      showError("❌ Failed to send notifications");
      console.log(error);
    }
  };

  // calculation for pie charts 
  // assume `today` is defined; normalize to day precision
  const today = dayjs().startOf("day");
  const cutoff = today.subtract(10, "day"); // >10 days past = expired

  let customersCount = policies.length;
  let dueCount = 0;  // endDate after today
  let overdueCount = 0;  // endDate on/before today but within last 10 days
  let expiredCount = 0;  // endDate more than 10 days before today
  let upcoming48hCount = 0;
  let renewalsData = []; // NEW: endDate is exactly +1 or +2 days from today

  policies.forEach((policy) => {
    const rawEnd =
      policy.endDate ||
      policy.policyDetails?.endDate ||
      policy.policyDetails?.end_date;

    const endDate = dayjs(rawEnd).startOf("day");
    if (!endDate.isValid()) return;

    if (endDate.isAfter(today)) {
      dueCount++;
      renewalsData.push(policy)
      const diffDays = endDate.diff(today, "day");
      if (diffDays === 1 || diffDays === 2) {
        upcoming48hCount++;
      }
    } else if (endDate.isSame(today) || endDate.isAfter(cutoff)) {
      overdueCount++;
    } else {
      expiredCount++;
    }
  });

  // ✅ Now you have: customersCount, dueCount, overdueCount, expiredCount, upcoming48hCount



  // ===== derived stats with date filter =====
  const groupedCompanies = {};
  const uniqueCustomers = new Set();
  let totalEarnings = 0;

  policies.forEach((p, index) => {
    const company = p.company || "Unknown";
    const customerId = p.userId || p.policyNumber || `anonymous-${index}`;
    
    // Check if policy should be included based on date filter
    let includeInEarnings = true;
    
    if (dateRange.startDate && dateRange.endDate) {
      const renewedDate = p.renewedDate || p.policyDetails?.renewedDate;
      const status = p.status || p.policyDetails?.status;
      
      if (renewedDate && status?.toLowerCase() === 'paid') {
        const policyDate = new Date(renewedDate);
        const startDate = new Date(dateRange.startDate);
        const endDate = new Date(dateRange.endDate);
        
        // Set time to start/end of day for accurate comparison
        policyDate.setHours(0, 0, 0, 0);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        
        includeInEarnings = policyDate >= startDate && policyDate <= endDate;
      } else {
        // If date filter is active but policy doesn't have renewedDate or status is not paid
        includeInEarnings = false;
      }
    }
    
    if (!groupedCompanies[company]) {
      groupedCompanies[company] = {
        name: company,
        status: "Active",
        customers: new Set(),
        policies: 0,
        earnings: 0,
      };
    }
    
    groupedCompanies[company].policies += 1;
    groupedCompanies[company].customers.add(customerId);
    uniqueCustomers.add(customerId);
    
    // Only add to earnings if policy meets filter criteria
    if (includeInEarnings) {
      const premium = p.policyDetails?.premium || 0;
      groupedCompanies[company].earnings += premium;
      totalEarnings += premium;
    }
    
  });


  // for top customers
  const totalCustomers = uniqueCustomers.size;

  const topCustomers = [...policies]
    .sort((a, b) => b.policyDetails.premium - a.policyDetails.premium) // highest first
    .slice(0, 5);

  console.log("Top 5 customers by premium:", topCustomers);

  const renewals = renewalsData

  const alerts = [
    {
      title: "Over Due With 48 hours",
      desc: `${upcoming48hCount} polices will over due in 48 hours`,
      tone: "warning",
      badge: "attention",
      icon: "bi-exclamation-triangle",
    },
    {
      title: "Policy Renewals",
      desc: `${dueCount} policies expiring soon`,
      tone: "danger",
      badge: "due",
      icon: "bi-bell",
    },
    {
      title: "Message & Notification Sent",
      desc: `You have ${[notificationsCount]} notifications today`,
      tone: "success",
      badge: "completed",
      icon: "bi-check-circle",
    },
    {
      title: "Expired Policies",
      desc: `${expiredCount} policies have expired and need attention`,
      tone: "danger",
      badge: "expired",
      icon: "bi-x-circle",
    },
  ];

  const initials = (name = "") =>
    name
      .split(" ")
      .map((s) => s[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();

  // ==== CHART HELPERS (works with your existing `policies`) ====

  const [range, setRange] = useState("6m"); // '3m' | '6m' | '12m'

  const getMonthKey = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

  const getLastNMonths = (n) => {
    const arr = [];
    const dt = new Date();
    dt.setDate(1);
    for (let i = n - 1; i >= 0; i--) {
      const d = new Date(dt.getFullYear(), dt.getMonth() - i, 1);
      arr.push({
        key: getMonthKey(d),
        label: d.toLocaleString("default", { month: "short" }),
      });
    }
    return arr;
  };

  // robust date parser from policy record
  const extractDate = (p) => {
    const raw =
      p?.policyDetails?.startDate ||
      p?.policyDetails?.issueDate ||
      p?.startDate ||
      p?.createdAt ||
      p?.created_at;

    const d = raw ? new Date(raw) : null;
    if (d && !isNaN(d.getTime())) return d;

    // fallback: bucket into current month
    return new Date();
  };

  // build monthly buckets from policies (selectedCategory is already applied upstream)
  const buildMonthlyData = (monthsCount) => {
    const months = getLastNMonths(monthsCount);
    const base = months.reduce((acc, m) => {
      acc[m.key] = { customers: new Set(), collected: 0 };
      return acc;
    }, {});

    policies.forEach((p, idx) => {
      const d = extractDate(p);
      const key = getMonthKey(d);
      if (!base[key]) return; // ignore outside of range
      const custId = p.userId || p.policyNumber || `anon-${idx}`;
      base[key].customers.add(custId);
      base[key].collected += p?.policyDetails?.premium || 0;
    });

    return months.map((m) => {
      const bucket = base[m.key] || { customers: new Set(), collected: 0 };
      const collected = bucket.collected;
      return {
        label: m.label,
        customers: bucket.customers.size,
        collected,
        due: Math.round(collected * 0.22), // same assumption you already use
      };
    });
  };

  const monthsMap = { "3m": 3, "6m": 6, "12m": 12 };
  const chartData = buildMonthlyData(monthsMap[range]);

  // convenience (today cards still use your existing totals)
  const pretty = (v) => v.toLocaleString();

  // Classic, animated pie like the screenshot
  const ClassicPie = ({ data, size = 260 }) => {
    // data: [{key, value, color}]
    const [t, setT] = React.useState(0); // 0 -> 1 animation progress

    // simple ease-out
    React.useEffect(() => {
      let raf, start;
      const dur = 900;
      const ease = (x) => 1 - Math.pow(1 - x, 3);
      const tick = (ts) => {
        if (!start) start = ts;
        const p = Math.min(1, (ts - start) / dur);
        setT(ease(p));
        if (p < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(raf);
    }, []);

    const total = Math.max(
      1,
      data.reduce((a, b) => a + (Number(b.value) || 0), 0)
    );

    const cx = size / 2;
    const cy = size / 2;
    const R = Math.min(cx, cy) - 20; // outer radius
    const toRad = (deg) => (deg * Math.PI) / 180;
    const polar = (r, a) => [cx + r * Math.cos(toRad(a)), cy + r * Math.sin(toRad(a))];

    const arcPath = (r, a0, a1) => {
      const [x0, y0] = polar(r, a0);
      const [x1, y1] = polar(r, a1);
      const large = a1 - a0 > 180 ? 1 : 0;
      return `M ${cx} ${cy} L ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1} Z`;
    };

    const labelInk = (hex) => {
      const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      if (!m) return "#fff";
      const [r, g, b] = [m[1], m[2], m[3]].map((h) => parseInt(h, 16) / 255);
      const lum = [r, g, b].map(v => (v <= .03928 ? v / 12.92 : Math.pow((v + .055) / 1.055, 2.4)));
      const L = 0.2126 * lum[0] + 0.7152 * lum[1] + 0.0722 * lum[2];
      return L > 0.6 ? "#334155" : "#ffffff";
    };

    let start = -90; // start at 12 o'clock
    return (
      <div className="pie-card">
        <div className="pie-wrap">
          <svg viewBox={`0 0 ${size} ${size}`} width="100%" height="100%">
            {data.map((d, i) => {
              const sweepFull = (d.value / total) * 360;
              const sweep = sweepFull * t; // animate all slices together
              const end = start + sweep;
              const mid = start + sweep / 2;
              const path = arcPath(R, start, end);

              // inner labels (percent)
              const [lx, ly] = polar(R * 0.58, mid);
              const pct = Math.round((d.value / total) * 100);

              // outside captions (like your screenshot)
              const [ox, oy] = polar(R + 30, mid);
              const anchor =
                mid > -90 && mid <= 90 ? "start" : mid > 90 && mid <= 270 ? "end" : "middle";

              start += sweepFull; // NOTE: accumulate with full sweep so final angles match target
              return (
                <g key={i} className="pie-slice">
                  <path d={path} fill={d.color} />
                  <text
                    x={lx}
                    y={ly}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize="13"
                    fontWeight="700"
                    fill={labelInk(d.color)}
                  >
                    {pct}%
                  </text>

                </g>
              );
            })}
          </svg>
        </div>

        <div className="pie-legend">
          {data.map((d) => (
            <div key={d.key} className="pie-legend__item">
              <span className="pie-dot" style={{ background: d.color }} />
              <span className="pie-label">{d.key}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // greeting + date (no API changes)
  const greeting = React.useMemo(() => {
    const hr = new Date().getHours();
    if (hr < 12) return "Good Morning";
    if (hr < 17) return "Good Afternoon";
    return "Good Evening";
  }, []);

const getTimeEmoji = () => {
  const hr = new Date().getHours();

  if (hr >= 5 && hr < 12) {
    return { emoji: "🌤️", cls: "emoji-morning" };
  }
  if (hr >= 12 && hr < 16) {
    return { emoji: "☀️", cls: "emoji-afternoon" };
  }
  return { emoji: "🌙", cls: "emoji-night" };
};

// const hour = new Date().getHours();

//   const getEmojiClass = () => {
//   if (hour >= 5 && hour < 12) return "emoji-morning";      // 🌤️
//   if (hour >= 12 && hour < 17) return "emoji-afternoon";   // ☀️
//   return "emoji-night";                                    // 🌙✨
// };

  const todayStr = React.useMemo(
    () =>
      new Date().toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      }),
    []
  );

  const formatName = (name) => {
    if (!name) return "User";

    return name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

const { emoji, cls } = getTimeEmoji();

//for getting the intial of the world
const getInitials = (name = "") => {
  if (!name) return "?";

  const parts = name.trim().split(" ").filter(Boolean);

  // Case 1: First + Last name available
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  // Case 2: Only first name → return first 2 letters
  return parts[0].substring(0, 2).toUpperCase();
};

  return (
    <div className="dashboard-layout d-flex">
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      {sidebarOpen && <div className="overlay" onClick={toggleSidebar}></div>}

      <div className="main-content flex-grow-1">
        <div className="agent-dashboard-section">
          <div className="agent-dashboard container py-4">
            {/* Header */}
            <div className="dashboard-header card border-0 shadow-sm rounded-4 mb-4">
              <div className="card-body d-flex align-items-center justify-content-between flex-wrap gap-3">
                {/* Left: avatar + greeting */}
                <div className="d-flex align-items-center gap-3">
                  <div className="text-center p-2 text-white" style={{background:"#066f7a", borderRadius:"50%", height:"40px", width:"40px", fontWeight:"700"}}>
                    {getInitials(user?.name)}
                  </div>
                  <div>
                    <div className="fw-bold dh-greet">
                      {greeting},
                      {" "}{formatName(user?.name)}
                      <span className={`time-emoji ${cls}`}>{emoji}</span>                
                    </div>
                    <div className="text-muted small">{todayStr}</div>
                  </div>
                </div>

                {/* Right: compact category selector */}
                <div className="category-select">
                  <label htmlFor="categorySwitch" className="category-select__label">
                    Insurance Category
                  </label>
                  <select
                    id="categorySwitch"
                    value={selectedCategory}
                    onChange={handleSwitchCategory}
                    className="form-select form-select-sm category-select__control"
                  >
                    <option value="Life Insurance">Life Insurance</option>
                    <option value="General Insurance">General Insurance</option>
                  </select>
                </div>
              </div>
            </div>


            {/* ==== Statistics(category-aware) ==== */}
            <div className="row g-3 mb-4 kpi-row">
              <div className="col-md-4">
                <div className="kpi-card">
                  <div className="kpi-head">
                    <span className="kpi-title">Total Customers</span>
                    <span className="kpi-icon kpi-icon--primary">
                      <i className="bi bi-people" />
                    </span>
                  </div>
                  <div className="kpi-value">{totalCustomers}</div>
                </div>
              </div>

              <div className="col-md-4">
                <div className="kpi-card" style={{ position: 'relative' }}>
                  <div className="kpi-head">
                    <span className="kpi-title">
                      Premium Collected
                      {(dateRange.startDate && dateRange.endDate) && (
                        <small className="d-block text-muted" style={{ fontSize: '0.7rem', marginTop: '4px', fontWeight: 'normal' }}>
                          {new Date(dateRange.startDate).toLocaleDateString('en-GB')} - {new Date(dateRange.endDate).toLocaleDateString('en-GB')}
                        </small>
                      )}
                    </span>
                    <span 
                      className="kpi-icon kpi-icon--success" 
                      style={{ cursor: 'pointer' }}
                      onClick={() => setShowCalendar(!showCalendar)}
                      title="Filter by date range"
                    >
                      <i className="bi bi-credit-card-2-front" />
                    </span>
                  </div>
                  <div className="kpi-value">₹{pretty(totalEarnings)}</div>
                  
                  {/* Date Range Picker Dropdown */}
                  {showCalendar && (
                    <>
                      <div 
                        style={{
                          position: 'fixed',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          zIndex: 999
                        }}
                        onClick={() => setShowCalendar(false)}
                      />
                      <div 
                        className="date-picker-dropdown"
                        style={{
                          position: 'absolute',
                          top: '100%',
                          right: '0',
                          zIndex: 1000,
                          backgroundColor: 'white',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                          borderRadius: '8px',
                          padding: '20px',
                          marginTop: '8px',
                          minWidth: '280px',
                          border: '1px solid #e5e7eb'
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="mb-3">
                          <label className="form-label" style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
                            Start Date
                          </label>
                          <input
                            type="date"
                            className="form-control"
                            value={dateRange.startDate || ''}
                            onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                            max={dateRange.endDate || new Date().toISOString().split('T')[0]}
                            style={{ fontSize: '0.875rem' }}
                          />
                        </div>
                        
                        <div className="mb-3">
                          <label className="form-label" style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
                            End Date
                          </label>
                          <input
                            type="date"
                            className="form-control"
                            value={dateRange.endDate || ''}
                            onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                            min={dateRange.startDate}
                            max={new Date().toISOString().split('T')[0]}
                            style={{ fontSize: '0.875rem' }}
                          />
                        </div>
                        
                        <div className="d-flex gap-2">
                          <button 
                            className="btn btn-sm btn-primary flex-grow-1"
                            onClick={() => setShowCalendar(false)}
                            style={{ fontSize: '0.875rem' }}
                          >
                            Apply Filter
                          </button>
                          <button 
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => {
                              setDateRange({ startDate: null, endDate: null });
                              setShowCalendar(false);
                            }}
                            style={{ fontSize: '0.875rem' }}
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="col-md-4">
                <div className="kpi-card">
                  <div className="kpi-head">
                    <span className="kpi-title">Premium Due</span>
                    <span className="kpi-icon kpi-icon--danger">
                      <i className="bi bi-exclamation-circle" />
                    </span>
                  </div>
                  <div className="kpi-value">₹{pretty(Math.round(totalEarnings * 0.22))}</div>
                </div>
              </div>
            </div>

            {/* Action Cards + Quick Actions + Pie */}
            <div className="row g-4 mb-3">
              <div className="col-lg-8">
                <div className="row g-4">
                  {/* Add Customer Manually */}
                  <div className="col-md-6">
                    <div className="p-4 Manual_Box rounded-4 shadow-sm bg-white d-flex flex-column justify-content-between">
                      <div className="d-flex align-items-center gap-3 mb-3">
                        <div className="icon-circle bg-opacity-10 rounded-4 p-3">
                          <i className="bi bi-person-plus fs-3"></i>
                        </div>
                        <h5 className="fw-bold mb-0">Add Manually</h5>
                      </div>
                      <p className="text-muted mb-3">
                        Create a new customer profile with detailed information.
                      </p>
                      <div>
                        <button
                          className="btn btn-link text-primary p-0 fw-semibold"
                          onClick={() => navigate("/agent/add-manual")}
                        >
                          Get Started <i className="bi bi-arrow-right-short fs-5"></i>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Import via Excel */}
                  <div className="col-md-6">
                    <div className="p-4 import__Box rounded-4 shadow-sm bg-white d-flex flex-column justify-content-between">
                      <div className="d-flex align-items-center gap-3 mb-3">
                        <div className="icon-circle bg-opacity-10 rounded-4 p-3">
                          <i className="bi bi-file-earmark-spreadsheet fs-3"></i>
                        </div>
                        <h5 className="fw-bold mb-0">Import via Excel</h5>
                      </div>
                      <p className="text-muted mb-3">
                        Upload and import multiple customer records in bulk.
                      </p>
                      <div>
                        <button
                          className="btn btn-link text-success p-0 fw-semibold"
                          onClick={() => navigate("/agent/import-excel")}
                        >
                          Upload File <i className="bi bi-arrow-right-short fs-5"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Actions (fills the width under the two cards) */}
                <div className="card border-0 Quick-Actions shadow-sm rounded-4 mt-4">
                  <div className="card-body p-3 p-md-4">
                    <h5 className="fw-bold mb-3">Quick Actions</h5>

                    <div className="row g-3">
                      {/* Premium Due (filled teal) */}
                      <div className="col-12 col-sm-6 col-xl-4">
                        <button
                          type="button"
                          className="qa-tile is-teal w-100"
                          onClick={() => navigate("/agent/due-payments")}
                        >
                          <i className="bi bi-calendar2-event" />
                          <span>Premium Due</span>
                        </button>
                      </div>

                      {/* Notifications (outline) */}
                      <div className="col-12 col-sm-6 col-xl-4">
                        <button
                          type="button"
                          className="qa-tile is-outline w-100"
                          onClick={() => navigate("/agent/notification")}
                        >
                          <i className="bi bi-bell" />
                          <span>Notifications</span>
                        </button>
                      </div>



                      {/* Customer List (filled cyan) */}
                      <div className="col-12 col-sm-6 col-xl-4">
                        <button
                          type="button"
                          className="qa-tile is-cyan w-100"
                          onClick={() => navigate("/agent/customers")}
                        >
                          <i className="bi bi-people" />
                          <span>Customer List</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* RIGHT: Pie card */}
              <div className="col-lg-4">
                <div className="p-4 rounded-4 shadow-sm bg-white h-100">
                  <h5 className="fw-bold mb-3">Overview Breakdown</h5>
                  <ClassicPie
                    data={[
                      { key: "Within48h", value: upcoming48hCount || 0, color: "#b91a1aff" },
                      { key: "Overdue", value: overdueCount || 0, color: "#0E2B57" },
                      { key: "Due", value: dueCount || 0, color: "#F4B340" },
                      { key: "Expired", value: expiredCount || 0, color: "#EF6A3A" },
                    ].map(slice => ({
                      ...slice,
                      value: slice.value === 0 ? 0.01 : slice.value, // ensure no slice is exactly 0
                    }))}
                  />
                </div>
              </div>

            </div>



            {/* NEW: Top Customers & Upcoming Renewals */}
            <div className="Top__customer row g-4 mb-4">
              {/* Top Customers + Alerts (left) */}
              <div className="col-lg-6">
                <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                  <div className="card-body p-4">
                    <h5 className="fw-bold mb-3">Top Customers</h5>
                    <ul className="top-list list-unstyled mb-0">
                      {topCustomers.map((c, idx) => (
                        <li key={idx} className="top-list__item">
                          <div className="d-flex align-items-center gap-3">
                            <div className="top-list__avatar">{initials(c.customerName)}</div>
                            <div className="min-w-0">
                              <div className="top-list__name text-truncate">{c.customerName}</div>
                              <div className="top-list__sub text-truncate">{c.company}</div>
                            </div>
                          </div>
                          <div className="top-list__amount">₹{c.policyDetails?.premium.toLocaleString()}</div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Notifications & Alerts */}
                <div className="mt-4">
                  <div className="card border-0 shadow-sm rounded-4">
                    <div className="card-body p-4">
                      <h5 className="fw-bold mb-3">Notifications &amp; Alerts</h5>
                      <div className="vstack gap-3">
                        {alerts.map((a, idx) => (
                          <div
                            key={idx}
                            className="d-flex align-items-center justify-content-between bg-light rounded-4 px-3 py-3"
                            style={{ background: "#f8fbfd" }}
                          >
                            <div className="d-flex align-items-center gap-3">
                              <div
                                className={`rounded-3 d-flex align-items-center justify-content-center text-${a.tone}`}
                                style={{
                                  width: 40,
                                  height: 40,
                                  background:
                                    a.tone === "warning"
                                      ? "#fff4d5"
                                      : a.tone === "success"
                                        ? "#dcfce7"
                                        : "#ffe4e6",
                                }}
                              >
                                <i className={`bi ${a.icon} fs-5`}></i>
                              </div>
                              <div>
                                <div className="fw-semibold">{a.title}</div>
                                <div className="text-muted small">{a.desc}</div>
                              </div>
                            </div>
                            <span
                              className={`badge rounded-pill ${a.tone === "success"
                                  ? "text-bg-success-subtle"
                                  : a.tone === "warning"
                                    ? "text-bg-warning-subtle"
                                    : "text-bg-danger-subtle"
                                }`}
                            >
                              {a.badge}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Upcoming Renewals (right) */}
              <div className="col-lg-6">
                <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                  <div className="card-body p-4">
                    <h5 className="fw-bold mb-3">Upcoming Renewals</h5>
                    <div className="renewals vstack gap-3">
                      {renewals.map((r, idx) => (
                        <div key={idx} className="renewals__card">
                          <div className="d-flex justify-content-between align-items-start">
                            <div className="min-w-0">
                              <div className="renewals__name text-truncate">{r.customerName}</div>
                              <div className="renewals__sub text-truncate">Policy: {r.policyNumber}</div>
                            </div>
                            <span
                              className={`renewals__status ${r.status === "Due Soon" ? "is-danger" : "is-warning"
                                }`}
                            >
                              {r.status}
                            </span>
                          </div>

                          <div className="renewals__meta">
                            <span className="d-inline-flex align-items-center gap-2">
                              <i className="bi bi-calendar2-week" /> {r.policyDetails?.endDate}
                            </span>
                            <span className="d-inline-flex align-items-center gap-2">
                              <i className="bi bi-telephone" /> {r.customerPhone}
                            </span>
                          </div>

                          <button
                            type="button"
                            className="renewals__btn btn btn-sm"
                            aria-label={`Send reminder to ${r.customerName}`}
                            onClick={() => {
                              setSelectedPerson(r);
                              setMessage(
                                `Dear ${r.customerName}, this is a reminder for your policy (${r.policyNumber}) renewal on ${r.policyDetails?.endDate}. Please take necessary action.`
                              );
                              setShowModal(true);
                            }}
                          >
                            Send Reminder
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <Modal show={showModal} onHide={() => setShowModal(false)} centered className="custom-message-modal">
                <Modal.Header closeButton className="border-0 pb-0">
                  <Modal.Title className="w-100 text-center fw-bold ">
                    ✉️ Send Personalized Message
                  </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                  <div className="p-4">
                    <h5 className="fw-bold mb-3">Send a Message to {selectedPerson?.customerName}</h5>
                    <textarea
                      rows={5}
                      className="form-control"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                    />
                  </div>
                </Modal.Body>

                <Modal.Footer>
                  <Button variant="secondary" onClick={() => setShowModal(false)}>
                    Cancel
                  </Button>
                  <Button variant="primary" onClick={() => { handleSendMessage(selectedPerson, message); setShowModal(false); }}>
                    Send Message
                  </Button>
                </Modal.Footer>
              </Modal>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

};

export default AgentDashboard;